import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { FiAlertCircle, FiArrowLeft } from 'react-icons/fi';
import { BottomNav } from '../components/layout/BottomNav';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/Button';
import { cancelActiveCheckoutSession, clearStoredCheckoutSession } from '../api/checkout';

export const Route = createFileRoute('/checkout/cancel')({
  validateSearch: (search: Record<string, unknown>) => ({
    session_id: typeof search.session_id === 'string' ? search.session_id : undefined,
    eventId: typeof search.eventId === 'string' ? search.eventId : undefined,
    email: typeof search.email === 'string' ? search.email : undefined,
  }),
  component: CheckoutCancelPage,
});

function CheckoutCancelPage() {
  const navigate = useNavigate();
  const { session_id: sessionId, eventId, email } = Route.useSearch();
  const [isClearing, setIsClearing] = useState(true);

  useEffect(() => {
    const clearSession = async () => {
      // Always clear local storage — even if the backend call fails or there's no session_id
      clearStoredCheckoutSession();

      if (!sessionId) {
        // No session to cancel on the backend, but that's fine — just show cancelled state
        setIsClearing(false);
        return;
      }

      try {
        await cancelActiveCheckoutSession({
          session_id: sessionId,
          email: email || undefined,
        });
      } catch (err) {
        // Backend may return "no session available" if the session already expired or was
        // already cancelled. This is not an error from the user's perspective — silently ignore.
        console.warn('Failed to cancel session on backend (already expired or not found):', err);
      } finally {
        setIsClearing(false);
      }
    };

    void clearSession();
  }, [sessionId, email]);

  const handleReturnToEvent = () => {
    if (eventId) {
      void navigate({ to: '/events/$eventId', params: { eventId } });
      return;
    }

    void navigate({ to: '/events/public' });
  };

  return (
    <div className="min-h-screen bg-black text-white pb-28">
      <Header />
      <main className="mx-auto max-w-3xl px-4 pt-28 md:px-8">
        <div className="rounded-4xl border border-white/8 bg-zinc-950/90 p-6 sm:p-10 text-center shadow-2xl">
          <div className="flex flex-col items-center gap-6">
            <div className="w-16 h-16 rounded-full flex items-center justify-center bg-amber-500/10">
              <FiAlertCircle size={34} className="text-amber-300" />
            </div>

            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">
                {isClearing ? 'Cancelling checkout...' : 'Checkout cancelled'}
              </h1>
              <p className="mt-2 text-sm sm:text-base text-zinc-400">
                {isClearing
                  ? 'Your reserved tickets are being released and your session will be cleared.'
                  : 'Your checkout session was cancelled. Your reserved tickets have been released and are available for others to purchase.'}
              </p>
            </div>

            <div className="flex flex-col gap-3 w-full max-w-sm">
              <Button
                className="rounded-full w-full"
                onClick={handleReturnToEvent}
                disabled={isClearing}
              >
                <FiArrowLeft className="mr-2" size={16} />
                {eventId ? 'Return to event' : 'Return to events'}
              </Button>
              <Button
                variant="outline"
                className="rounded-full border-white/10 hover:bg-white/5 w-full"
                onClick={() => void navigate({ to: '/events/public' })}
                disabled={isClearing}
              >
                Browse all events
              </Button>
            </div>
          </div>
        </div>
      </main>
      <BottomNav />
    </div>
  );
}
